import React from 'react';
import { Link } from 'react-router-dom';
import OptimizedContentImage from './OptimizedContentImage';
import {
  formatEventDateLong,
  formatEventTimeRange,
  formatEventTypeLabel,
  type EventDateTimeLike,
} from '../../utils/eventFormatting';

type EventPostCardEvent = EventDateTimeLike & {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  featured_image?: string | null;
  event_type?: string | null;
  location?: string | null;
  price?: string | null;
  featured?: boolean | null;
};

interface EventPostCardProps {
  event: EventPostCardEvent;
  imageUrl?: string | null;
  from?: 'home' | 'events';
}

const getEventTypeColor = (type?: string | null) => {
  switch ((type ?? '').toUpperCase()) {
    case 'LIVE_SHOW':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'SPECIAL_EVENT':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'BROADCAST':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    default:
      return 'bg-gray-500/20 text-slate-200 border-gray-500/30';
  }
};

const EventPostCard: React.FC<EventPostCardProps> = ({
  event,
  imageUrl,
  from,
}) => {
  const dateLabel = formatEventDateLong(event);
  const timeLabel = formatEventTimeRange(event);
  const eventTypeLabel = formatEventTypeLabel(event.event_type);
  const locationLabel = (event.location ?? '').trim();
  const hasLocation = locationLabel.length > 0;
  const descriptionLabel = (event.description ?? '').trim();
  const hasDescription = descriptionLabel.length > 0;
  const priceLabel = (event.price ?? '').trim();
  const hasPrice = priceLabel.length > 0;

  return (
    <Link
      to={`/events/${event.slug}`}
      state={from ? { from } : undefined}
      className="group"
    >
      <div className="bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700 hover:border-red-500/50 transition-all duration-300 group-hover:transform group-hover:scale-[1.02]">
        {imageUrl && (
          <div className="aspect-video overflow-hidden bg-gray-900/30">
            <OptimizedContentImage
              src={imageUrl}
              alt={event.title}
              className="w-full h-full"
              fit="contain"
              imageClassName="w-full h-full"
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          </div>
        )}

        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium border ${getEventTypeColor(
                event.event_type
              )}`}
            >
              {eventTypeLabel}
            </span>
            {event.featured && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                Featured
              </span>
            )}
          </div>

          <h3 className="font-semibold text-white mb-3 group-hover:text-red-400 transition-colors line-clamp-2">
            {event.title}
          </h3>

          <div className="space-y-2 text-sm text-slate-200">
            <div className="flex items-center gap-2">
              <span aria-hidden>📅</span>
              <span>{dateLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <span aria-hidden>🕒</span>
              <span>{timeLabel}</span>
            </div>
            {hasLocation && (
              <div className="flex items-center gap-2">
                <span aria-hidden>📍</span>
                <span className="break-words">{locationLabel}</span>
              </div>
            )}
          </div>

          {hasDescription && (
            <p className="mt-3 text-gray-400 text-sm line-clamp-3">
              {descriptionLabel}
            </p>
          )}

          {hasPrice && (
            <div className="mt-3 text-green-400 font-semibold">{priceLabel}</div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default EventPostCard;

